import * as Rx from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { Collections } from '../collections.model';
import {
  assertNever,
  compareMultipleFields,
  fromPairs,
  isDefined,
  isEqual,
  jsonStableStringify,
  mapValues,
  partition,
  sameElements,
  setDiff,
  toNumber,
  toObservable,
} from './helpers';
import {
  isInequalityParam,
  NullQuery,
  NullQueryObject,
  Ordering,
  Query,
  QueryObject,
} from './query';
import {
  ArrayElement,
  ArrayEqualsQueryParam,
  Entities,
  Entity,
  InListQueryParam,
  List,
  ListIntersectsQueryParam,
  Lists,
  MapCoordinates,
  NearbyQueryParamValue,
  Path,
  QueryParam,
  QueryParams,
  RawQuery,
  Reference,
  StoreFeatureState,
  StoreState,
} from './store.model';

function paramMatchesEntity<T>(entity: Entity<T>, param: QueryParam<T>) {
  if (param.name in entity.entity) {
    const entityValue = entity.entity[param.name];

    switch (param.condition) {
      case '==':
        return isEqual(entityValue, param.value);
      case '<':
        return entityValue < param.value;
      case '<=':
        return entityValue <= param.value;
      case '>=':
        return entityValue >= param.value;
      case '>':
        return entityValue > param.value;
      case 'in':
        return param.values.some(value => isEqual(entityValue, value));
      case 'equalsIgnoreCase':
        return param.value.toUpperCase() === String(entityValue).toUpperCase();
      case 'includes':
        return ((entityValue as unknown) as ArrayElement<T[keyof T]>[]).some(
          value => isEqual(param.value, value),
        );
      case 'intersects':
        return param.values.some(paramValue =>
          ((entityValue as unknown) as ArrayElement<T[keyof T]>[]).some(value =>
            isEqual(paramValue, value),
          ),
        );
      case 'equalsIgnoreOrder':
        return param.values.every(paramValue =>
          ((entityValue as unknown) as ArrayElement<T[keyof T]>[]).includes(
            paramValue,
          ),
        );
      case 'nearby': {
        const distance = haversineDistance(
          param.value,
          (entityValue as unknown) as MapCoordinates, // Not sure if we can avoid the type cast
        );
        return distance <= param.value.range;
      }
      default:
        return assertNever(param);
    }
  }

  return false;
}

/**
 * Checks if an entity matches a query's params.
 * If there's no params then it does so trivially.
 */
export function entityMatchesQueryParams<T>(
  entity: Entity<T>,
  params?: QueryParams<T>,
) {
  return params
    ? Object.values(params).every(
        (clauses: NonNullable<QueryParams<T>[keyof T]>) =>
          isValidInterval(clauses)
            ? clauses.every(param => paramMatchesEntity(entity, param))
            : clauses.some(param => paramMatchesEntity(entity, param)),
      )
    : true;
}

/** Checks if query params form a valid bounded interval like a < x < b */
function isValidInterval<T>([firstParam, secondParam]: ReadonlyArray<
  QueryParam<T>
>) {
  return (
    firstParam &&
    secondParam &&
    isInequalityParam(firstParam) &&
    isInequalityParam(secondParam) &&
    firstParam.condition.startsWith('>') &&
    secondParam.condition.startsWith('<') &&
    firstParam.value <= secondParam.value
  );
}

/**
 * Gets a list of entities which match a particular query. If the data isn't
 * available for a particular query it will return the "best guess" by applying
 * the query to the default list in the store.
 *
 * Note: Not for general use - only intended for datastore internals.
 *
 * @param data Is the entity after collection and authId has been indexed
 * @param ref the reference for the requested data.
 * @param defaultOrder The default order of the collection
 *
 * @returns An array of entities, or undefined if none matched
 */
export function selectEntitiesForQuery<K extends keyof Collections>(
  storeSlice: StoreState[K][string],
  ref: Reference<K>,
  defaultOrder: Ordering<K>,
): ReadonlyArray<Collections[K]> | undefined {
  if (!storeSlice) {
    return undefined;
  }

  const queryString = stringifyQuery(ref.query);

  // If queried list exists, return that.
  if (storeSlice.lists[queryString]) {
    return storeSlice.lists[queryString].ids.map(
      id => storeSlice.entities[id].entity,
    );
  }

  // New query, so filter default list first.
  if (
    (!ref.order || isEqual(ref.order, defaultOrder)) &&
    storeSlice.lists.default
  ) {
    const matchingObjects = storeSlice.lists.default.ids
      .map(id => storeSlice.entities[id])
      .filter(isDefined)
      .filter(entityObj =>
        entityMatchesQueryParams(entityObj, ref.query && ref.query.params),
      )
      .slice(0, ref.query && ref.query.limit)
      .map(entityObj => entityObj.entity);

    return matchingObjects.length > 0 ? matchingObjects : undefined;
  }

  return undefined;
}

export function stringifyQuery<T>(query: RawQuery<T> | undefined) {
  if (query) {
    interface KeyConditionValue {
      key: string;
      condition: string;
      value: any;
    }

    const { params, ...pagination } = query;

    if (!params) {
      // null query - this should never appear in the store
      return 'null';
    }

    const stringifiedParams: KeyConditionValue[] = Object.entries(
      params,
    ).reduce(
      (acc, [key, clauses]: [string, NonNullable<QueryParams<T>[keyof T]>]) => {
        const stringifiedClauses = clauses.map(param => {
          // When stringifying pick something that can be concatenated without ambiguity
          switch (param.condition) {
            case '==':
            case '<=':
            case '<':
            case '>=':
            case '>':
              return { key, condition: param.condition, value: param.value };
            case 'equalsIgnoreCase':
              return { key, condition: '~ignoreCase~', value: param.value };
            case 'equalsIgnoreOrder':
              return { key, condition: '~ignoreOrder~', value: param.values };
            case 'includes':
              return { key, condition: '~includes~', value: param.value };
            case 'in':
              return { key, condition: '~in~', value: param.values };
            case 'intersects':
              return { key, condition: '~intersects~', value: param.values };
            case 'nearby':
              return { key, condition: '~nearby~', value: param.value };
            default:
              return assertNever(param);
          }
        });

        return [...acc, ...stringifiedClauses];
      },
      [] as KeyConditionValue[],
    );

    const entries = Object.entries(pagination)
      .map(
        ([key, value]) =>
          ({ key, condition: '==', value } as KeyConditionValue),
      )
      .filter(({ key, condition, value }) => value !== undefined)
      .concat(...stringifiedParams)
      .map(
        ({ key, condition, value }) =>
          `${key}${condition}${jsonStableStringify(value, true)}`,
      )
      .sort()
      .join(';');

    return entries || 'default';
  }

  // undefined query - this can happen for no-query #list calls or #object(s) calls
  return 'default';
}

// Compatible with RawQuery
interface FlattenedQuery<K extends keyof Collections> {
  readonly limit: number | undefined;
  readonly params: QueryParams<Collections[K]> | undefined;
  readonly order: Ordering<K> | undefined;
}

/**
 * Turns a query function passed by datastore clients to an Observable of
 * its params and other properties (limit, order).
 *
 * There are 3 cases:
 * - queryFn is `undefined` or default (query => query) -> params to `{}`
 * - queryFn contains explicit clauses (where, limit, orderBy) -> params passed through
 *   - 'where' clauses with an empty 'in' parameter (i.e. `[]`) are filtered out
 * - queryFn is the null query -> params to `undefined`
 *
 * @returns flattened query used by #list in the datastore
 */
export function flattenQuery<K extends keyof Collections>(
  queryFn?: (q: Query<K>) => Query<K> | Rx.Observable<Query<K> | NullQuery>,
): Rx.Observable<FlattenedQuery<K>> {
  const queryObject$: Rx.Observable<QueryObject<K> | NullQueryObject> = queryFn
    ? toObservable(queryFn(Query.emptyQuery())).pipe(map(q => q.query))
    : Rx.of({
        limit$: Rx.of(undefined),
        params$: Rx.of({}),
        order$: Rx.of(undefined),
      });

  return queryObject$.pipe(
    // TODO: TypeScript complains without the type parameters
    switchMap<QueryObject<K> | NullQueryObject, FlattenedQuery<K>>(query =>
      isNullQuery(query)
        ? Rx.of({ limit: undefined, params: undefined, order: undefined })
        : Rx.combineLatest(query.limit$, query.params$, query.order$).pipe(
            map(([limit, params, order]) => ({ limit, params, order })),
          ),
    ),
    map(flattenedQuery => ({
      ...flattenedQuery,
      params: removeInvalidQueryParams(flattenedQuery.params),
    })),
    distinctUntilChanged((x, y) => stringifyQuery(x) === stringifyQuery(y)),
  );
}

function isNullQuery<K extends keyof Collections>(
  q: QueryObject<K> | NullQueryObject,
): q is NullQueryObject {
  return (
    q.limit$ === undefined && q.params$ === undefined && q.order$ === undefined
  );
}

/**
 * Removes `in` clauses from parameters where the value is an empty array `[]`.
 * If this would result in no parameters remaining, return `undefined`
 * instead of the empty params object `{}`.
 *
 * The purpose of this is to achieve the same thing as the null query, making the
 * collection Observable emit an empty list immediately without triggering a
 * network request. E.g. `query.where('id', 'in', [])` => `[]` immediately.
 *
 * To avoid this behaviour, you must explicitly check for an empty 'in' clause,
 * by chaining `notEmpty` or otherwise.
 */
// TODO: Consider moving this elsewhere to keep `flattenQuery` focused, maybe
// a separate query object transformation step
function removeInvalidQueryParams<T>(
  params?: QueryParams<T>,
): QueryParams<T> | undefined {
  // Don't touch params from a null query or default query
  if (!params || Object.keys(params).length === 0) {
    return params;
  }

  const validParamsEntries: [
    string,
    QueryParams<T>[keyof T]
  ][] = Object.entries(params).filter(
    ([key, clauses]: [string, NonNullable<QueryParams<T>[keyof T]>]) => {
      const clausesWithoutEmptyIns = clauses.filter(
        param => !(param.condition === 'in' && param.values.length === 0),
      );

      return clausesWithoutEmptyIns.length > 0;
    },
  );

  // TODO: Convert this to fromPairs
  const parsed: QueryParams<T> = validParamsEntries.reduce(
    (
      parsedParams: QueryParams<T>,
      [key, param]: [string, QueryParams<T>[keyof T]],
    ) => ({
      ...parsedParams,
      [key]: param,
    }),
    {},
  );

  return Object.keys(parsed).length > 0 ? parsed : undefined;
}

/**
 * Merges the unwrapped entities/items of a collection, only overwriting the ones
 * that have changed.
 */
function mergeRawEntities<K extends keyof Collections>(
  currentEntities: Entities<Collections[K]>,
  newEntities: Entities<Collections[K]>,
) {
  // The `newEntities`, keeping the unwrapped object from `currentEntities` but
  // with updated metadata (timeFetched/updated) if they compare deep equal,
  const updatedNewEntities = mapValues(newEntities, (newEntity, key) =>
    key in currentEntities &&
    isEqual(currentEntities[key].entity, newEntity.entity)
      ? {
          ...newEntity,
          entity: currentEntities[key].entity,
        }
      : newEntity,
  );

  return {
    ...currentEntities,
    ...updatedNewEntities,
  };
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 */
export function mergeEntities<K extends keyof Collections>(
  state: StoreFeatureState<K>,
  entities: Entities<Collections[K]>,
  order: Ordering<K>,
  ref: Reference<K>,
): StoreFeatureState<K> {
  const slice = state[ref.path.authUid];
  const storeSlice =
    slice && slice.entities
      ? slice
      : {
          entities: {},
          lists: {},
        };

  const queryString = stringifyQuery(ref.query);

  const updatedEntities = mergeRawEntities(storeSlice.entities, entities);

  const newListIds = Object.keys(entities)
    .filter(key => {
      const matches = entityMatchesQueryParams(
        entities[key],
        ref.query ? ref.query.params : undefined,
      );
      if (!matches) {
        const invalidEntityMessage = `Attempted to merge invalid entity into list for ${
          ref.path.collection
        }:
the backend returned an object that did not match the given query`;
        console.error(invalidEntityMessage, ref.query, entities[key]);
        // only throw 10% of the time in case we are using this "legitimately"
        if (toNumber(ref.path.authUid) % 10 === 9) {
          throw new Error(invalidEntityMessage);
        }
      }
      return matches;
    })
    .sort((id1, id2) =>
      compareMultipleFields<Collections[K]>(order)(
        updatedEntities[id1].entity,
        updatedEntities[id2].entity,
      ),
    );

  const query: RawQuery<Collections[K]> = ref.query || { params: {} };

  const otherListsUpdated: Lists<K> = fromPairs(
    Object.entries(storeSlice.lists)
      .filter(([key]) => key !== queryString)
      .map(
        ([key, list]) =>
          [key, updateList(list, entities, updatedEntities)] as [
            string,
            List<K>
          ],
      ),
  );

  return {
    ...state,
    [ref.path.authUid]: {
      entities: updatedEntities,
      lists: {
        ...otherListsUpdated,
        [queryString]: { ids: newListIds, query, order },
      },
    },
  };
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 * TODO T40096: This is a websocket version of mergeEntities, which has more
 * logic right now to apply the websocket data on the right place of the store
 * slice. Maybe we can merge this with mergeEntities again?
 */
export function mergeWebsocketEntities<K extends keyof Collections>(
  state: StoreFeatureState<K>,
  entities: Entities<Collections[K]>,
  ref: Reference<K>,
): StoreFeatureState<K> {
  const slice = state[ref.path.authUid];
  const storeSlice =
    slice && slice.entities
      ? slice
      : {
          entities: {},
          lists: {},
        };

  const updatedEntities = mergeRawEntities(storeSlice.entities, entities);

  if (!storeSlice.lists) {
    return state;
  }

  const updatedLists: Lists<K> = fromPairs(
    Object.entries(storeSlice.lists).map(
      ([key, list]) =>
        [key, updateList(list, entities, updatedEntities)] as [string, List<K>],
    ),
  );

  return {
    ...state,
    [ref.path.authUid]: {
      entities: updatedEntities,
      lists: { ...storeSlice.lists, ...updatedLists },
    },
  };
}

function updateList<K extends keyof Collections>(
  list: List<K>,
  entities: Entities<Collections[K]>,
  updatedEntities: Entities<Collections[K]>,
): List<K> {
  const { query, order } = list;
  const { params, offset, limit } = query;

  // If the offset is non-zero we don't add it to the new list.
  if (offset) {
    return list;
  }

  // Existing entity ids of the old list
  const currEntityIds = list ? Array.from(list.ids) : [];

  // Find which entities modified by the websocket now match (or don't match)
  // the query. These should be added (or removed) from the list
  const [matchingIds, nonMatchingIds] = partition(
    Object.entries(entities),
    ([entityId, entity]) => entityMatchesQueryParams(entity, params),
  ).map(entityEntries => entityEntries.map(([entityId]) => entityId));

  // The new list is the old list plus any matching entity ids, minus any
  // non-matching entity ids, while ensuring all ids are unique
  const filteredEntityIds = setDiff(
    currEntityIds.concat(matchingIds),
    nonMatchingIds,
  );

  // Finally, apply limit to the ids before merging into list
  const updatedEntityIds = [...filteredEntityIds]
    .sort((id1, id2) =>
      compareMultipleFields<Collections[K]>(order)(
        updatedEntities[id1].entity,
        updatedEntities[id2].entity,
      ),
    )
    .slice(0, limit);

  return { ...list, ids: updatedEntityIds };
}

/**
 * Add/overwrite a list of entities to the datastore that are from a websocket.
 * If you instead want to modify an existing object use
 * `transformWebsocketEntities`.]
 * If your transformer maps to `undefined` then the entity will be ignored.
 */
export function addWebsocketEntities<K extends keyof Collections, O>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<O>,
  transform: (a: O) => Collections[K] | undefined,
  ref: Reference<K>,
): StoreFeatureState<K>;
export function addWebsocketEntities<K extends keyof Collections, O, E>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<O>,
  transform: (a: O, extra: E) => Collections[K] | undefined,
  ref: Reference<K>,
  extra: E,
): StoreFeatureState<K>;
export function addWebsocketEntities<K extends keyof Collections, O, E>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<O>,
  transform: (a: O, extra?: E) => Collections[K] | undefined,
  ref: Reference<K>,
  extra?: E,
): StoreFeatureState<K> {
  return mergeWebsocketEntities<K>(
    state,
    transformOptionalWebsocketObjects(list, transform, extra),
    ref,
  );
}

/**
 * Update entities in the collection by running a transformation on them.
 * If an entity is not found in the store, it is ignored.
 *
 * @param state slice of store state representing a collection
 * @param list ids of the entities to update
 * @param transform applied to every entity with an id in `list`
 * @param ref a Datastore reference, links back to the dispatching action
 */
export function updateWebsocketEntities<K extends keyof Collections>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<string> | ReadonlyArray<number>,
  transform: (a: Collections[K]) => Collections[K],
  ref: Reference<K>,
): StoreFeatureState<K> {
  return mergeWebsocketEntities<K>(
    state,
    transformOptionalWebsocketObjects<Collections[K], string | number>(
      list,
      id => {
        const object = pluckItemFromRawStoreFeature(state, ref.path, id);
        return object ? transform(object) : undefined;
      },
    ),
    ref,
  );
}

/**
 * Update all entities in the collection by running a transformation on them.
 * The transform function should return the original object if it does not need
 * to be updated.
 *
 * Note: Due to performance considerations, you should strongly favour
 * `updateWebsocketEntities` over this variant. This variant should only be used
 * when access to ALL the entities is required to perform an update (e.g.
 * filtering them based on some property of the entity).
 * If you are unsure, use the other one.
 *
 * @param state store state representing a collection
 * @param transform applied to every entity in the collection
 * @param ref a Datastore reference, links back to the dispatching action
 */
export function updateAllEntities<K extends keyof Collections>(
  state: StoreFeatureState<K>,
  transform: (a: Collections[K]) => Collections[K],
  ref: Reference<K>,
): StoreFeatureState<K> {
  const slice = state[ref.path.authUid];
  return mergeWebsocketEntities<K>(
    state,
    transformOptionalWebsocketObjects<Collections[K], string | number>(
      Object.keys(slice ? slice.entities : {}),
      id => {
        const object = pluckItemFromRawStoreFeature(state, ref.path, id);
        if (!object) {
          return undefined;
        }

        const transformedObject = transform(object);
        // Transformed object is the same, skip over this entity
        return transformedObject === object ? undefined : transformedObject;
      },
    ),
    ref,
  );
}

interface ObjectWithId {
  readonly id: number | string;
}

export function transformObjects<T extends ObjectWithId, O>(
  list: ReadonlyArray<O> | { readonly [id: number]: O | undefined } | undefined,
  transform: (a: NonNullable<O>) => T,
): Entities<T>;

export function transformObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O> | { readonly [id: number]: O | undefined } | undefined,
  transform: (a: NonNullable<O>, extra: E) => T,
  extra: E,
): Entities<T>;

export function transformObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O> | { readonly [id: number]: O | undefined } | undefined,
  transform: (a: NonNullable<O>, extra?: E) => T,
  extra?: E,
): Entities<T> {
  if (!list) {
    return {};
  }

  const actualList: ReadonlyArray<NonNullable<O>> = (Array.isArray(list)
    ? list
    : Object.values<O | undefined>(list)
  ).filter(isDefined);

  return actualList
    .map(o => transform(o, extra))
    .filter(isDefined)
    .map(entity => ({
      [entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: Entities<T>, item) => ({ ...acc, ...item }), {}); // Switch to fromPairs.
}

/**
 * This should only be used for websocket transformers as we shouldn't skip
 * objects when transforming get requests or it will break pagination.
 * It should not be exported.
 */
function transformOptionalWebsocketObjects<T extends ObjectWithId, O>(
  list: ReadonlyArray<O>,
  transform: (a: O) => T | undefined,
): Entities<T>;
function transformOptionalWebsocketObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O>,
  transform: (a: O, extra: E) => T | undefined,
  extra: E,
): Entities<T>;
function transformOptionalWebsocketObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O>,
  transform: (a: O, extra?: E) => T | undefined,
  extra?: E,
): Entities<T> {
  return list
    .map(o => transform(o, extra))
    .filter(isDefined)
    .map(entity => ({
      [entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: Entities<T>, item) => ({ ...acc, ...item }), {});
}

export function pathsEqual<K extends keyof Collections>(
  a: Path<K>,
  b: Path<K>,
) {
  return (
    a.collection === b.collection &&
    a.authUid === b.authUid &&
    sameElements(a.ids || [], b.ids || [])
  );
}

/**
 * Check if both QueryParams are equal down to the QueryParam values.
 * @param a QueryParams to compare.
 * @param b Another QueryParams to compare.
 * @return boolean Whether or not it's the same.
 */
function rawQueryEqual<T>(a: RawQuery<T>, b: RawQuery<T>) {
  const aQuery = stringifyQuery(a);
  const bQuery = stringifyQuery(b);

  return aQuery === bQuery;
}

export function referencesEqual<K extends keyof Collections>(
  a: Reference<K>,
  b: Reference<K>,
) {
  return (
    pathsEqual(a.path, b.path) &&
    ((a.query === undefined && b.query === undefined) ||
      (a.query !== undefined &&
        b.query !== undefined &&
        a.query.offset === b.query.offset &&
        a.query.limit === b.query.limit &&
        rawQueryEqual(a.query, b.query)))
  );
}

export function pluckItemFromRawStore<K extends keyof Collections>(
  store: StoreState,
  path: Path<K>,
  id: string | number,
): Collections[K] | undefined {
  const slice = store[path.collection][path.authUid];
  return slice && slice.entities[id] ? slice.entities[id].entity : undefined;
}

export function pluckItemFromRawStoreFeature<K extends keyof Collections>(
  storeFeature: StoreFeatureState<K>,
  path: Path<K>,
  id: string | number,
): Collections[K] | undefined {
  const slice = storeFeature[path.authUid];
  if (!slice || !slice.entities[id]) {
    return undefined;
  }
  return slice.entities[id].entity;
}

export function removeEntityById<K extends keyof Collections>(
  ref: Reference<K>,
  state: StoreFeatureState<K>,
  id: string | number,
): StoreFeatureState<K> {
  const data = state[ref.path.authUid] || { lists: {}, entities: {} };
  const entities = { ...data.entities };
  delete entities[id];

  // Remove the deleted ID from any list
  const lists = Object.entries(data.lists).reduce(
    (obj, [queryString, list]) => {
      const newIds = (list && list.ids ? list.ids : []).filter(
        listId => listId.toString() !== id.toString(),
      );

      return { ...obj, [queryString]: { ...list, ids: newIds } };
    },
    {} as Lists<K>,
  );

  return { ...state, [ref.path.authUid]: { entities, lists } };
}

/**
 * Gets the query param value of a specific query param name and returns it in
 * an array.
 *
 * Note that for the `intersects` and `equalsIgnoreTrue` param conditions, the
 * param value (which already must be an array) is wrapped inside another array.
 * E.g. For the query `query => query.where('jobs', 'intersects', [1, 2, 3])`:
 * `getQueryParamValue(query, 'jobs') => [[1, 2, 3]]`. See TODO below on why
 *
 * @param query The query.
 * @param name The key of the entity for the query.
 * @param valueSelector (optional) Maps the param value into a new value. This
 * can be useful when the param value is an object but you want to retrieve a
 * nested property.
 *
 * @returns An array of the values.
 */
// TODO: Ideally this return type is consistently a flat array, i.e. if T[K] is
// an array, don't wrap it in another array. This could be achieved by conditional
// types pending resolution of https://github.com/Microsoft/TypeScript/issues/28917.
// In the meantime, we do this to ensure consistency with the type system and other
// `where` conditions which only work with array fields, e.g. 'includes',
// 'equalsIgnoreOrder' and 'intersects'
export function getQueryParamValue<T, K extends keyof T, R>(
  query: RawQuery<T> | undefined,
  name: K,
  valueSelector: (param: QueryParam<T, K>) => R,
): ReadonlyArray<R>;
export function getQueryParamValue<T, K extends keyof T>(
  query: RawQuery<T> | undefined,
  name: K,
): ReadonlyArray<T[K]>;
export function getQueryParamValue<T, K extends keyof T, R>(
  query: RawQuery<T> | undefined,
  name: K,
  valueSelector?: (param: QueryParam<T, K>) => R,
): ReadonlyArray<any> {
  if (!query || !query.params) {
    return [];
  }

  const clauses = query.params[name];
  if (!clauses) {
    return [];
  }

  const paramValues = clauses.reduce(
    (acc, param) => {
      const value = valueSelector
        ? valueSelector(param)
        : isArrayParamValue(param)
        ? param.values
        : param.value;

      // 'in' + 'includes' are special cases to make actual return value consistent
      // with the return type inferred by TypeScript
      switch (param.condition) {
        case 'in':
          return [...acc, ...(value as ReadonlyArray<any>)];
        case 'includes':
          return [...acc, [value]];
        default:
          return [...acc, value];
      }
    },
    [] as ReadonlyArray<any>,
  );

  return paramValues;
}

function isArrayParamValue<T, K extends keyof T>(
  param: QueryParam<T, K>,
): param is
  | InListQueryParam<T, K>
  | ListIntersectsQueryParam<T, K>
  | ArrayEqualsQueryParam<T, K> {
  const isArrayParamCondition =
    param.condition === 'in' ||
    param.condition === 'intersects' ||
    param.condition === 'equalsIgnoreOrder';

  if (isArrayParamCondition && !Array.isArray((param as any).values)) {
    throw new Error(
      `Query parameter on field '${param.name}' has condition '${
        param.condition
      }' but does not have an array value.`,
    );
  }

  return isArrayParamCondition;
}

/**
 * Get the query param value for a nearby query and returns the first
 * nearby query param coordinate object, or undefined if the query did not
 * have the nearby condition specified.
 *
 * Note: Will throw if the given nearby query param was not valid.
 *
 * @param {RawQuery<T> | undefined} query
 * @param {K} name The key of the entity for the query with has the
 * map coordinates defined.
 *
 * @returns {NearbyQueryParamValue | undefined}
 */
export function getNearbyQueryParamValue<T, K extends keyof T>(
  query: RawQuery<T> | undefined,
  name: K,
): NearbyQueryParamValue | undefined {
  return getQueryParamValue(query, name, param => {
    if (param.condition !== 'nearby') {
      return undefined;
    }

    if (
      typeof param.value.latitude !== 'number' ||
      typeof param.value.longitude !== 'number'
    ) {
      throw new Error(
        'Must supply numeric latitude and longitude with nearby queries.',
      );
    }

    return param.value;
  })[0];
}

/**
 * An implementation of the Haversine formula used to calculate "great circle"
 * distance.
 *
 * Example:
 *
 * Sydney Airport to London Heathrow Airport
 * `haversineDistance(51.469916, -0.454353, -33.939887, 151.175276) / 1000`
 * => ~17_019 km
 *
 * @see https://en.wikipedia.org/wiki/Haversine_formula
 *
 * @returns {number} Approximate distance in metres between two spatial points on Earth.
 */
export function haversineDistance(
  { latitude: latDeg1, longitude: lonDeg1 }: MapCoordinates,
  { latitude: latDeg2, longitude: lonDeg2 }: MapCoordinates,
) {
  const toRadians = (degree: number) => (degree / 180.0) * Math.PI;

  const lat1 = toRadians(latDeg1);
  const lon1 = toRadians(lonDeg1);
  const lat2 = toRadians(latDeg2);
  const lon2 = toRadians(lonDeg2);

  // Radius of earth in meters
  const earthRadius = 6_371_000;

  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.asin(Math.sqrt(a));

  return earthRadius * c;
}
